import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  AcademicCapIcon, 
  UserIcon, 
  EnvelopeIcon, 
  LockClosedIcon, 
  CheckCircleIcon,
  ArrowRightIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ExclamationCircleIcon,
  CheckCircleIcon as CheckCircleSolidIcon
} from '@heroicons/react/24/outline';
import api, { fetchWithToken } from '../services/api';
import { useAuthStore } from '../services/api';

const Register = () => {
  const [activeTab, setActiveTab] = useState('student');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
    username: '',
    phone: '',
    // Öğrenci alanları
    examTypeId: '',
    // Öğretmen alanları
    expertise: '',
    subExpertise: '', // Alt kategori
    experience: '',
    bio: '',
  });
  const location = useLocation();
  const navigate = useNavigate();
  
  // Form doğrulama state'leri
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [usernameValid, setUsernameValid] = useState(null);
  const [usernameChecking, setUsernameChecking] = useState(false);
  
  // Timeout ref'i tanımla (debounce için)
  const usernameCheckTimeout = useRef(null);

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);

  // Exam Types state'leri
  const [examTypes, setExamTypes] = useState([]);
  const [examTypesLoading, setExamTypesLoading] = useState(false);
  const [examTypesError, setExamTypesError] = useState(null);

  const [emailValid, setEmailValid] = useState(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const emailCheckTimeout = useRef(null);

  const [phoneValid, setPhoneValid] = useState(null);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const phoneCheckTimeout = useRef(null);

  const setAccessToken = useAuthStore(state => state.setAccessToken);

  // Kategorileri backend'den çek (Öğretmen için)
  useEffect(() => {
    if (activeTab === 'teacher') {
      setCategoriesLoading(true);
      fetch('/v1/categories/root')
        .then(res => res.json())
        .then(rest => {
          setCategories(Array.isArray(rest?.data) ? rest.data : []);
          setCategoriesError(null);
        })
        .catch(err => {
          setCategories([]);
          setCategoriesError('Uzmanlık alanları yüklenemedi');
        })
        .finally(() => setCategoriesLoading(false));
    }
  }, [activeTab]);

  // Exam Types'ları backend'den çek (Öğrenci için)
  useEffect(() => {
    if (activeTab === 'student') {
      setExamTypesLoading(true);
      fetch('/v1/study/exam-types/parents-null')
        .then(res => res.json())
        .then(rest => {
          setExamTypes(Array.isArray(rest?.data) ? rest.data : []);
          setExamTypesError(null);
        })
        .catch(err => {
          setExamTypes([]);
          setExamTypesError('Sınav türleri yüklenemedi');
        })
        .finally(() => setExamTypesLoading(false));
    }
  }, [activeTab]);

  // Alt kategorileri yükle
  const loadSubCategories = async (parentId) => {
    setSubCategoriesLoading(true);
    try {
      const response = await fetch(`/v1/categories/${parentId}/subcategories`);
      const rest = await response.json();
      setSubCategories(Array.isArray(rest?.data) ? rest.data : []);
    } catch (err) {
      console.error('Alt kategoriler yüklenemedi:', err);
      setSubCategories([]);
    } finally {
      setSubCategoriesLoading(false);
    }
  };

  // Ana kategori değiştiğinde alt kategorileri yükle
  const handleExpertiseChange = (e) => {
    const parentId = e.target.value;
    setFormData(prev => ({
      ...prev,
      expertise: parentId,
      subExpertise: '' // Alt kategori seçimini sıfırla
    }));
    
    if (parentId) {
      loadSubCategories(parentId);
    } else {
      setSubCategories([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let formattedValue = value;
    
    // Telefon numarası formatlaması
    if (name === 'phone') {
      // Sadece rakamları al
      const numbersOnly = value.replace(/[^0-9]/g, '');
      
      // Eğer boş değilse ve 0 ile başlamıyorsa, 0 ekle
      if (numbersOnly && !numbersOnly.startsWith('0')) {
        formattedValue = `0${numbersOnly}`;
      } else {
        formattedValue = numbersOnly;
      }
      
      // 11 karakterden fazlasını kes
      formattedValue = formattedValue.slice(0, 11);

      // Telefon numarası 11 haneye ulaştığında kontrol et
      if (formattedValue.length === 11) {
        setPhoneChecking(true);
        setPhoneValid(null);
        if (phoneCheckTimeout.current) clearTimeout(phoneCheckTimeout.current);
        phoneCheckTimeout.current = setTimeout(() => {
          checkPhoneAvailability(formattedValue);
        }, 500);
      } else {
        setPhoneValid(null);
        setPhoneChecking(false);
      }
    }
    
    // İsim ve soyisim için ilk harfleri büyük yapma
    if (name === 'firstName' || name === 'lastName') {
      formattedValue = value
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    
    // Kullanıcı adı için tüm harfleri küçük yapma ve boşlukları kaldırma
    if (name === 'username') {
      formattedValue = value.toLowerCase().replace(/\s/g, '');
    }

    // Input değerini güncelle
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : formattedValue
    }));
    
    // Kullanıcı adı uzunluk kontrolü ve validasyonu
    if (name === 'username') {
      if (formattedValue.length > 100) {
        setErrors(prev => ({
          ...prev,
          username: 'Kullanıcı adı en fazla 100 karakter olabilir'
        }));
        return;
      } else {
        setErrors(prev => ({ ...prev, username: null }));
      }

      if (formattedValue.length >= 3) {
        setUsernameChecking(true);
        setUsernameValid(null);
        if (usernameCheckTimeout.current) {
          clearTimeout(usernameCheckTimeout.current);
        }
        usernameCheckTimeout.current = setTimeout(() => {
          checkUsernameAvailability(formattedValue);
        }, 500);
      } else {
        setUsernameValid(null);
      }
    }
    
    // E-posta kontrolü için debounce
    if (name === 'email' && /\S+@\S+\.\S+/.test(value)) {
      setEmailChecking(true);
      setEmailValid(null);
      if (emailCheckTimeout.current) clearTimeout(emailCheckTimeout.current);
      emailCheckTimeout.current = setTimeout(() => {
        checkEmailAvailability(value);
      }, 500);
    } else if (name === 'email') {
      setEmailValid(null);
    }

    // İlgili hata mesajını temizle
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Kullanıcı adı kontrol fonksiyonu
  const checkUsernameAvailability = async (username) => {
    try {
      const response = await fetch(`/v1/user/check/username?username=${username}`);
      const data = await response.json();
      if (data && typeof data.data === "boolean") {
        setUsernameValid(data.data);
      } else {
        setUsernameValid(false);
      }
    } catch (error) {
      setUsernameValid(false);
    } finally {
      setUsernameChecking(false);
    }
  };

  // E-posta kontrol fonksiyonu
  const checkEmailAvailability = async (email) => {
    try {
      const response = await fetch(`/v1/user/check/email?email=${encodeURIComponent(email)}`);
      const data = await response.json();
      if (data && typeof data.data === "boolean") {
        setEmailValid(data.data);
      } else {
        setEmailValid(false);
      }
    } catch {
      setEmailValid(false);
    } finally {
      setEmailChecking(false);
    }
  };

  // Telefon kontrol fonksiyonu
  const checkPhoneAvailability = async (phone) => {
    try {
      const response = await fetch(`/v1/user/check/phone?phone=${encodeURIComponent(phone)}`);
      const data = await response.json();
      if (data && typeof data.data === "boolean") {
        setPhoneValid(data.data);
      } else {
        setPhoneValid(false);
      }
    } catch {
      setPhoneValid(false);
    } finally {
      setPhoneChecking(false);
    }
  };

  // Şifre gücü ve kuralları kontrolü
  const passwordStrength = () => {
    const { password } = formData;
    if (!password) return { strength: 0, label: '', color: 'bg-gray-300', valid: false };

    // En az 1 büyük, 1 küçük harf, 1 rakam, 1 özel karakter ve min 6 karakter
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const isLong = password.length >= 6;

    let valid = hasUpper && hasLower && hasDigit && hasSpecial && isLong;

    if (!isLong) return { strength: 25, label: 'Zayıf', color: 'bg-red-500', valid };
    if (password.length < 8) return { strength: 50, label: 'Orta', color: 'bg-yellow-500', valid };
    if (password.length < 10) return { strength: 75, label: 'İyi', color: 'bg-blue-500', valid };
    return { strength: 100, label: valid ? 'Güçlü' : 'Kurallara Uygun Değil', color: valid ? 'bg-green-500' : 'bg-red-500', valid };
  };

  const pwStrength = passwordStrength();

  // Backend için formatlanmış veriyi hazırla
  const prepareDataForSubmission = () => {
    if (activeTab === 'teacher') {
      return {
        userName: formData.username,
        name: formData.firstName,
        surname: formData.lastName,
        password: formData.password,
        email: formData.email,
        phone: formData.phone ? String(formData.phone) : "",
        category: formData.subExpertise ? [Number(formData.subExpertise)] : [], // Alt kategori ID'sini gönder
        experience: Number(formData.experience) || 0,
        biography: formData.bio,
      };
    }
    // Öğrenci için güncellenmiş yapı
    return {
      userName: formData.username,
      name: formData.firstName,
      surname: formData.lastName,
      password: formData.password,
      email: formData.email,
      phone: formData.phone ? String(formData.phone) : "",
      examTypeId: Number(formData.examTypeId) || null,
    };
  };

  // Form doğrulama fonksiyonu
  const validateForm = () => {
    const newErrors = {};

    // Ad ve soyad min 3, max 25 karakter
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'İsim alanı zorunludur';
    } else if (formData.firstName.length < 3) {
      newErrors.firstName = 'İsim en az 3 karakter olmalı';
    } else if (formData.firstName.length > 25) {
      newErrors.firstName = 'İsim en fazla 25 karakter olmalı';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Soyisim alanı zorunludur';
    } else if (formData.lastName.length < 3) {
      newErrors.lastName = 'Soyisim en az 3 karakter olmalı';
    } else if (formData.lastName.length > 25) {
      newErrors.lastName = 'Soyisim en fazla 25 karakter olmalı';
    }

    // Kullanıcı adı min 3, max 20 karakter
    if (!formData.username.trim()) {
      newErrors.username = 'Kullanıcı adı zorunludur';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Kullanıcı adı en az 3 karakter olmalı';
    } else if (formData.username.length > 20) {
      newErrors.username = 'Kullanıcı adı en fazla 20 karakter olmalı';
    } else if (usernameValid === false) {
      newErrors.username = 'Bu kullanıcı adı zaten kullanımda';
    }

    // E-posta format kontrolü
    if (!formData.email.trim()) {
      newErrors.email = 'E-posta alanı zorunludur';
    } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi giriniz';
    } else if (emailValid === false) {
      newErrors.email = 'Bu e-posta zaten kullanımda';
    }

    // Şifre kuralları
    if (!formData.password) {
      newErrors.password = 'Şifre zorunludur';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Şifre en az 6 karakter olmalıdır';
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Şifre en az 1 büyük harf içermelidir';
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password = 'Şifre en az 1 küçük harf içermelidir';
    } else if (!/\d/.test(formData.password)) {
      newErrors.password = 'Şifre en az 1 rakam içermelidir';
    } else if (!/[^A-Za-z0-9]/.test(formData.password)) {
      newErrors.password = 'Şifre en az 1 özel karakter içermelidir';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Şifre tekrarı zorunludur';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Şifreler eşleşmiyor';
    }

    // Telefon numarası kontrolü
    if (formData.phone) {
      if (!/^0\d{10}$/.test(formData.phone)) {
        newErrors.phone = 'Telefon numarası 0 ile başlamalı ve 11 haneli olmalıdır';
      } else if (phoneValid === false) {
        newErrors.phone = 'Bu telefon numarası zaten kullanımda';
      }
    }

    // Öğrenci için sınav türü kontrolü
    if (activeTab === 'student') {
      if (!formData.examTypeId) {
        newErrors.examTypeId = 'Sınav türü seçilmelidir';
      }
    }

    // Eğitmen alanları sadece UI doğrulaması için - backend'e gönderilmez
    if (activeTab === 'teacher') {
      if (!formData.expertise) {
        newErrors.expertise = 'Uzmanlık alanı seçilmelidir';
      }
      if (!formData.experience) {
        newErrors.experience = 'Deneyim belirtilmelidir';
      } else if (Number(formData.experience) > 40) {
        newErrors.experience = 'Deneyim en fazla 40 yıl olabilir';
      }
      if (!formData.bio || formData.bio.length < 10) {
        newErrors.bio = 'Özgeçmiş en az 10 karakter olmalıdır';
      } else if (formData.bio.length > 200) {
        newErrors.bio = 'Özgeçmiş en fazla 200 karakter olmalı';
      }
    }

    if (!formData.agreeTerms) {
      newErrors.agreeTerms = 'Kullanım şartlarını kabul etmelisiniz';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form gönderim işlemi
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!validateForm()) {
      return;
    }
  
    setIsSubmitting(true);
  
    try {
      const registerData = prepareDataForSubmission();
      const endpoint =
        activeTab === 'teacher'
          ? '/v1/auth/register/teacher'
          : '/v1/auth/register/student';
  
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerData)
      });
      const data = await response.json();
  
      if (data?.success && data?.data && data.data.userId) {
        // Doğrudan localStorage'a kaydetme! Bunun yerine sessionStorage'a geçici olarak kaydet
        const pendingUser = {
          id: data.data.userId,
          username: data.data.username,
          role: data.data.role,
          profileImageUrl: data.data.profileImageUrl || null
        };
        sessionStorage.setItem('pendingUser', JSON.stringify(pendingUser));
        // Navbar'ın güncellenmesi için custom event tetikleME!
        if (data.data.accessToken) setAccessToken(data.data.accessToken);

        window.updateProfileImageInLocalStorage = (profileImageUrl) => {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          user.profileImageUrl = profileImageUrl;
          localStorage.setItem('user', JSON.stringify(user));
          window.dispatchEvent(new Event('user-updated'));
        };

        navigate(`/verify-email?email=${encodeURIComponent(formData.email)}&registration=true`);
      } else {
        const errorMsg = data?.message || data?.data?.message || data?.data || 'Kayıt başarısız. Lütfen bilgilerinizi kontrol edin.';
        alert(errorMsg);
      }
    } catch (error) {
      let errorMsg = 'Kayıt sırasında bir hata oluştu. Lütfen daha sonra tekrar deneyin.';
      if (error.response && error.response.data) {
        const errData = error.response.data;
        errorMsg = errData.message || errData.data || errorMsg;
      }
      alert(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // URL'den tab parametresi ile teacher tabını aç
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'teacher') setActiveTab('teacher');
    if (tab === 'student') setActiveTab('student');
  }, [location.search]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 py-6 sm:py-8 md:py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="max-w-4xl w-full grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8 relative">
        {/* Sol taraf - Mobilde görünen basitleştirilmiş bilgi paneli */}
        <div className="md:hidden bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <AcademicCapIcon className="h-8 w-8 text-white/90" />
            <h1 className="text-xl font-bold">DersMaster'a Hoş Geldiniz</h1>
          </div>
          <p className="text-sm text-white/80 mb-4">Öğrenme yolculuğunuza bugün başlayın.</p>
          <div className="flex justify-between items-center">
            <p className="text-xs text-white/80">Zaten hesabınız var mı?</p>
            <Link to="/login" className="text-white font-medium text-sm bg-white/20 px-3 py-1.5 rounded-lg hover:bg-white/30 transition-colors">
              Giriş Yap
            </Link>
          </div>
        </div>
        
        {/* Sol taraf - Tablet/Masaüstü Bilgi Paneli */}
        <div className="hidden md:flex md:col-span-2 bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-6 lg:p-8 text-white flex-col justify-between shadow-xl">
          <div>
            <div className="mb-6">
              <AcademicCapIcon className="h-10 w-10 lg:h-12 lg:w-12 text-white/90" />
              <h1 className="text-2xl lg:text-3xl font-bold mt-4">DersMaster'a Hoş Geldiniz</h1>
              <p className="mt-2 text-white/80 text-sm lg:text-base">Öğrenme yolculuğunuza bugün başlayın.</p>
            </div>
            
            <div className="space-y-3 lg:space-y-4 mt-6 lg:mt-8">
              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 lg:h-6 lg:w-6 text-blue-200 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-sm lg:text-base">Binlerce Kurs</h3>
                  <p className="text-xs lg:text-sm text-white/70">Her seviyeye ve ilgi alanına uygun içerikler</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 lg:h-6 lg:w-6 text-blue-200 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-sm lg:text-base">Uzman Eğitmenler</h3>
                  <p className="text-xs lg:text-sm text-white/70">Alanında uzman eğitmenlerden öğrenin</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-5 w-5 lg:h-6 lg:w-6 text-blue-200 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-sm lg:text-base">Esnek Öğrenme</h3>
                  <p className="text-xs lg:text-sm text-white/70">İstediğiniz zaman, istediğiniz yerden öğrenin</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6">
            <p className="text-sm text-white/80">Zaten hesabınız var mı?</p>
            <Link to="/login" className="flex items-center text-white font-medium mt-2 group hover:opacity-90 transition-all">
              Giriş Yap 
              <ChevronRightIcon className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
        
        {/* Sağ taraf - Kayıt Formu */}
        <div className="md:col-span-3 bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-lg">
          <div className="mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Hesap Oluştur</h2>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mt-1">Seçtiğiniz role göre kayıt olun</p>
          </div>
          
          {/* Rol seçimi sekmeleri - Büyük dokunmatik alanlar */}
          <div className="flex space-x-2 mb-6 sm:mb-8">
            <button
              onClick={() => setActiveTab('student')}
              className={`flex-1 py-3 px-3 sm:px-4 rounded-xl flex items-center justify-center gap-2 transition-all ${
                activeTab === 'student'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <UserIcon className="h-5 w-5" />
              <span className="font-medium text-sm sm:text-base">Öğrenci</span>
            </button>
            
            <button
              onClick={() => setActiveTab('teacher')}
              className={`flex-1 py-3 px-3 sm:px-4 rounded-xl flex items-center justify-center gap-2 transition-all ${
                activeTab === 'teacher'
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <UserGroupIcon className="h-5 w-5" />
              <span className="font-medium text-sm sm:text-base">Eğitmen</span>
            </button>
          </div>
          
          {/* Kayıt Formu - Mobile optimized scrolling */}
          <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(100vh-16rem)] sm:max-h-none pr-1 -mr-1 space-y-4">
            <div className="space-y-4">
              {/* Ad Soyad - Stack on very small screens */}
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ad
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      maxLength={25}
                      className={`w-full px-4 py-3 rounded-lg border ${errors.firstName ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all text-sm sm:text-base`}
                      placeholder="Adınız"
                    />
                    {errors.firstName && (
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                      </div>
                    )}
                  </div>
                  {errors.firstName && (
                    <p className="mt-1 text-xs text-red-500">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Soyad
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      maxLength={25}
                      className={`w-full px-4 py-3 rounded-lg border ${errors.lastName ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all text-sm sm:text-base`}
                      placeholder="Soyadınız"
                    />
                    {errors.lastName && (
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                      </div>
                    )}
                  </div>
                  {errors.lastName && (
                    <p className="mt-1 text-xs text-red-500">{errors.lastName}</p>
                  )}
                </div>
              </div>
              
              {/* Kullanıcı Adı - Yeni Eklenen */}
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Kullanıcı Adı
                </label>
                <div className="relative">
                  <UserIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3" />
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    maxLength={20}
                    className={`w-full pl-10 pr-10 py-3 rounded-lg border ${
                      errors.username 
                        ? 'border-red-500 dark:border-red-500' 
                        : usernameValid === true 
                          ? 'border-green-500 dark:border-green-500' 
                          : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all text-sm sm:text-base`}
                    placeholder="kullanici_adi"
                  />
                  {usernameChecking && (
                    <div className="absolute right-3 top-3">
                      <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    </div>
                  )}
                  {!usernameChecking && usernameValid === true && (
                    <div className="absolute right-3 top-3">
                      <CheckCircleSolidIcon className="h-5 w-5 text-green-500" />
                    </div>
                  )}
                  {errors.username && (
                    <div className="absolute right-3 top-3">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                    </div>
                  )}
                </div>
                {errors.username ? (
                  <p className="mt-1 text-xs text-red-500">{errors.username}</p>
                ) : !usernameChecking && usernameValid === true && (
                  <p className="mt-1 text-xs text-green-500">Bu kullanıcı adı kullanılabilir</p>
                )}
                {!errors.username && !usernameChecking && usernameValid === false && (
                  <p className="mt-1 text-xs text-red-500">Bu kullanıcı adı zaten kullanımda, lütfen başka bir kullanıcı adı deneyin.</p>
                )}
              </div>
              
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  E-posta Adresi
                </label>
                <div className="relative">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className={`w-full pl-10 pr-10 py-3 rounded-lg border ${
                      errors.email
                        ? 'border-red-500 dark:border-red-500'
                        : emailValid === true
                          ? 'border-green-500 dark:border-green-500'
                          : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all text-sm sm:text-base`}
                    placeholder="ornek@email.com"
                  />
                  {emailChecking && (
                    <div className="absolute right-3 top-3">
                      <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    </div>
                  )}
                  {!emailChecking && emailValid === true && (
                    <div className="absolute right-3 top-3">
                      <CheckCircleSolidIcon className="h-5 w-5 text-green-500" />
                    </div>
                  )}
                  {errors.email && (
                    <div className="absolute right-3 top-3">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                    </div>
                  )}
                </div>
                {errors.email ? (
                  <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                ) : !emailChecking && emailValid === true && (
                  <p className="mt-1 text-xs text-green-500">Bu e-posta kullanılabilir</p>
                )}
                {!errors.email && !emailChecking && emailValid === false && (
                  <p className="mt-1 text-xs text-red-500">Bu e-posta zaten kullanımda, lütfen başka bir e-posta deneyin.</p>
                )}
              </div>

              {/* Telefon Numarası - Yeni Eklenen */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Telefon Numarası
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 rounded-lg border ${
                      errors.phone
                        ? 'border-red-500 dark:border-red-500'
                        : phoneValid === true
                          ? 'border-green-500 dark:border-green-500'
                          : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all text-sm sm:text-base`}
                    placeholder="0 5XX XXX XX XX"
                  />
                  {phoneChecking && (
                    <div className="absolute right-3 top-3">
                      <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    </div>
                  )}
                  {!phoneChecking && phoneValid === true && (
                    <div className="absolute right-3 top-3">
                      <CheckCircleSolidIcon className="h-5 w-5 text-green-500" />
                    </div>
                  )}
                  {errors.phone && (
                    <div className="absolute right-3 top-3">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                    </div>
                  )}
                </div>
                {errors.phone ? (
                  <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
                ) : !phoneChecking && phoneValid === true && (
                  <p className="mt-1 text-xs text-green-500">Bu telefon numarası kullanılabilir</p>
                )}
                {!errors.phone && !phoneChecking && phoneValid === false && (
                  <p className="mt-1 text-xs text-red-500">Bu telefon numarası zaten kullanımda, lütfen başka bir numara deneyin.</p>
                )}
              </div>

              {/* Öğrenci için Sınav Türü Seçimi */}
              {activeTab === 'student' && (
                <div>
                  <label htmlFor="examTypeId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Sınav Türü <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <DocumentTextIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3" />
                    <select
                      id="examTypeId"
                      name="examTypeId"
                      value={formData.examTypeId}
                      onChange={handleInputChange}
                      required={activeTab === 'student'}
                      className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                        errors.examTypeId 
                          ? 'border-red-500 dark:border-red-500' 
                          : 'border-gray-300 dark:border-gray-600'
                      } bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all text-sm sm:text-base`}
                      disabled={examTypesLoading}
                    >
                      <option value="">Sınav türünüzü seçin</option>
                      {examTypes.map(examType => (
                        <option key={examType.id} value={examType.id}>
                          {examType.name}
                        </option>
                      ))}
                    </select>
                    {errors.examTypeId && (
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                      </div>
                    )}
                  </div>
                  {examTypesLoading && (
                    <p className="mt-1 text-xs text-gray-500">Sınav türleri yükleniyor...</p>
                  )}
                  {examTypesError && (
                    <p className="mt-1 text-xs text-red-500">{examTypesError}</p>
                  )}
                  {errors.examTypeId && (
                    <p className="mt-1 text-xs text-red-500">{errors.examTypeId}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Hedeflediğiniz sınav türünü seçerek size özel içeriklere erişebilirsiniz.
                  </p>
                </div>
              )}
              
              {/* Şifre */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Şifre
                </label>
                <div className="relative">
                  <LockClosedIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3" />
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    className={`w-full pl-10 pr-10 py-3 rounded-lg border ${errors.password ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all text-sm sm:text-base`}
                    placeholder="En az 8 karakter"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password ? (
                  <p className="mt-1 text-xs text-red-500">{errors.password}</p>
                ) : (
                  // Şifre güç göstergesi
                  <div className="mt-1.5">
                    <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${pwStrength.color} transition-all duration-300`} 
                        style={{ width: `${pwStrength.strength}%` }} 
                      />
                    </div>
                    <p className={`text-xs mt-1 ${pwStrength.valid ? 'text-green-500' : 'text-red-500'}`}>
                      {pwStrength.label}
                      {!pwStrength.valid && formData.password && (
                        <>
                          {" - Şifre en az 1 büyük, 1 küçük harf, 1 rakam ve 1 özel karakter içermelidir."}
                        </>
                      )}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Şifre tekrar */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Şifre Tekrar
                </label>
                <div className="relative">
                  <LockClosedIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3" />
                  <input
                    type={showPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    className={`w-full pl-10 pr-4 py-3 rounded-lg border ${errors.confirmPassword ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all text-sm sm:text-base`}
                    placeholder="Şifrenizi tekrar girin"
                  />
                </div>
                {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1.5">Şifreler eşleşmiyor</p>
                )}
                {errors.confirmPassword && (
                  <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>
                )}
              </div>
              
              {/* Eğitmen için ek alanlar */}
              {activeTab === 'teacher' && (
                <div className="space-y-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">Eğitmen Bilgileri</h3>
                  
                  {/* Uzmanlık Alanı */}
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="expertise" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Ana Uzmanlık Alanı
                      </label>
                      <select
                        id="expertise"
                        name="expertise"
                        value={formData.expertise}
                        onChange={handleExpertiseChange}
                        required={activeTab === 'teacher'}
                        className={`w-full px-4 py-3 rounded-lg border ${errors.expertise ? 'border-red-500' : 'border-gray-300'} bg-white dark:bg-gray-700`}
                        disabled={categoriesLoading}
                      >
                        <option value="">Ana kategori seçin</option>
                        {(Array.isArray(categories) ? categories : []).map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    {formData.expertise && (
                      <div>
                        <label htmlFor="subExpertise" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Alt Uzmanlık Alanı
                        </label>
                        <select
                          id="subExpertise"
                          name="subExpertise"
                          value={formData.subExpertise}
                          onChange={handleInputChange}
                          required={activeTab === 'teacher'}
                          className={`w-full px-4 py-3 rounded-lg border ${errors.subExpertise ? 'border-red-500' : 'border-gray-300'} bg-white dark:bg-gray-700`}
                          disabled={subCategoriesLoading}
                        >
                          <option value="">Alt kategori seçin</option>
                          {subCategories.map(subCat => (
                            <option key={subCat.id} value={subCat.id}>{subCat.name}</option>
                          ))}
                        </select>
                        {subCategoriesLoading && (
                          <div className="mt-2 text-sm text-gray-500">Alt kategoriler yükleniyor...</div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Deneyim */}
                  <div>
                    <label htmlFor="experience" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Deneyim (Yıl)
                    </label>
                    <input
                      type="number"
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      min="0"
                      max="40"
                      required={activeTab === 'teacher'}
                      className={`w-full px-4 py-3 rounded-lg border ${errors.experience ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all text-sm sm:text-base`}
                      placeholder="Yıl olarak deneyiminiz"
                    />
                    {errors.experience && (
                      <p className="mt-1 text-xs text-red-500">{errors.experience}</p>
                    )}
                  </div>
                  
                  {/* Kısa Özgeçmiş */}
                  <div>
                    <label htmlFor="bio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Kendinizi Kısaca Tanıtın
                    </label>
                    <textarea
                      id="bio"
                      name="bio"
                      value={formData.bio}
                      onChange={handleInputChange}
                      rows="3"
                      maxLength={200}
                      required={activeTab === 'teacher'}
                      className={`w-full px-4 py-3 rounded-lg border ${errors.bio ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all text-sm sm:text-base`}
                      placeholder="Örneğin: Uzman Matematik Öğretmeni "
                    />
                    <div className="flex justify-between items-center mt-1">
                      {errors.bio && (
                        <p className="text-xs text-red-500">{errors.bio}</p>
                      )}
                      <span className="text-xs text-gray-400 ml-auto">{formData.bio.length}/200</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Kullanım Şartları */}
              <div className="flex items-start mt-4">
                <div className="flex items-center h-5">
                  <input
                    id="agreeTerms"
                    name="agreeTerms"
                    type="checkbox"
                    checked={formData.agreeTerms}
                    onChange={handleInputChange}
                    required
                    className={`focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded ${errors.agreeTerms ? 'border-red-500' : ''}`}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="agreeTerms" className={`${errors.agreeTerms ? 'text-red-500' : 'text-gray-700 dark:text-gray-300'} text-xs sm:text-sm`}>
                    <span>
                      <Link to="/terms" className="text-blue-600 dark:text-blue-400 hover:underline">Kullanım şartlarını</Link> ve{' '}
                      <Link to="/privacy" className="text-blue-600 dark:text-blue-400 hover:underline">Gizlilik politikasını</Link> kabul ediyorum
                    </span>
                  </label>
                  {errors.agreeTerms && (
                    <p className="mt-1 text-xs text-red-500">{errors.agreeTerms}</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Kayıt Ol Butonu */}
            <button
              type="submit"
              disabled={
                isSubmitting ||
                (formData.username && formData.username.length >= 3 && usernameValid === null) ||
                (formData.email && /\S+@\S+\.\S+/.test(formData.email) && emailValid === null) ||
                (formData.phone && formData.phone.length >= 10 && phoneValid === null)
              }
              className={`mt-6 w-full flex justify-center items-center px-6 py-3 sm:py-3.5 border border-transparent text-base font-medium rounded-xl text-white shadow-sm ${
                activeTab === 'student'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-purple-600 hover:bg-purple-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                (isSubmitting ||
                  (formData.username && formData.username.length >= 3 && usernameValid === null) ||
                  (formData.email && /\S+@\S+\.\S+/.test(formData.email) && emailValid === null) ||
                  (formData.phone && formData.phone.length >= 10 && phoneValid === null))
                  ? 'opacity-70 cursor-not-allowed'
                  : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Kaydediliyor...
                </>
              ) : (
                <>
                  {activeTab === 'student' ? 'Öğrenci Olarak Kayıt Ol' : 'Eğitmen Olarak Kayıt Ol'}
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </>
              )}
            </button>

            {/* Bilgi notları */}
            <div className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
              <p>Kayıt ol butonuna tıklayarak, hesap oluşturma sürecini başlatırsınız.</p>
              <p className="mt-1">Yardıma mı ihtiyacınız var? <Link to="/contact" className="text-blue-600 dark:text-blue-400 hover:underline">Destek ekibimize ulaşın</Link></p>
            </div>

            {/* Mobil için Login Link */}
            <div className="mt-3 text-center md:hidden">
              <span className="text-gray-600 dark:text-gray-400 text-sm">Zaten hesabınız var mı?</span>{' '}
              <Link to="/login" className="text-blue-600 dark:text-blue-400 font-medium hover:underline text-sm">
                Giriş Yap
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;